import {
  BIOME_IDS,
  BIOMES,
  type BiomeId,
  biomeAt,
  BOSS_MOB_IDS,
  BOSSES,
  COMPANY_QUARTER,
  MOBS,
  type ZoneDefinition,
} from '@shefira/shared';

import { isAction } from '../settings/keybindings.js';
import { closeButton, hudShortcutButton } from './hud.js';
import {
  levelLabel,
  type MinimapMarker,
  paintEventMark,
  paintQuestMarks,
  paintQuestTarget,
  portalMark,
} from './minimap.js';
import { eventAgenda } from './world-event-agenda.js';
import { uiFormat, uiText } from './text.js';
import type { MapPlayer } from './world-map-players.js';
import './world-map.css';
import { kitTitle } from './kit.js';
import { paintPixels } from './pixel-canvas.js';
import {
  coverScale,
  EXPLORE_SCALE,
  type ExploreControls,
  exploreControls,
  type ExploreTab,
  levelNear,
  MapPan,
  zoomLevels,
} from './world-map-explorer.js';

/**
 * The world map, opened with M: the whole zone at once, cut into its biomes,
 * each named with the levels it is meant for, the villages and the places
 * of interest named on it, and the player's own position. A legend lists
 * the biomes from the gentlest to the harshest and says how each one sits
 * against the player's level, so a player knows where to go next.
 *
 * Display only, like the minimap: it reads the zone's data and the level
 * the server already sends to the owner; it decides nothing.
 */

export interface WorldMapOptions {
  zone: ZoneDefinition;
  /** The zone painted at one pixel a tile (the minimap's picture). */
  picture: HTMLCanvasElement;
}

/** How a biome's band sits against the player's level. */
export type Challenge = 'easy' | 'fit' | 'hard' | 'deadly';

/** Each lair's boss by the lair's name, as the landmarks carry it. */
const BOSS_OF_LAIR = new Map(BOSS_MOB_IDS.map((id) => [BOSSES[id].lair, MOBS[id]] as const));

const CHALLENGE_LABELS: Record<Challenge, string> = {
  easy: 'Facile',
  fit: 'À ton niveau',
  hard: 'Difficile',
  deadly: 'Dangereux',
};

/** Room left around the map for the legend and the frame, in CSS pixels. */
const LEGEND_WIDTH = 300;
const CHROME_HEIGHT = 140;
/**
 * The same on a small screen (world-map.css, same breakpoints): a thinner
 * frame, a narrower legend beside the map on a phone on its side, under it
 * on a phone held upright (where it keeps at least STACKED_LEGEND_HEIGHT).
 */
const COMPACT_QUERY = '(max-width: 719px), (max-height: 539px)';
const STACKED_QUERY = '(max-width: 559px)';
const COMPACT_CHROME = { width: 72, height: 72 };
/** Upright, the title sits above the map rather than beside it. */
const STACKED_TITLE_HEIGHT = 42;
const COMPACT_LEGEND_WIDTH = 232;
const STACKED_LEGEND_HEIGHT = 170;
const MAX_SCALE = 4;
/**
 * Where the map fills the screen and moves under the finger instead
 * (ADR 0086): any small screen, and any touch screen. A larger window
 * joins them when its frame would draw the world under EXPLORE_SCALE.
 */
const EXPLORE_QUERY = `${COMPACT_QUERY}, (hover: none) and (pointer: coarse)`;
/** Wide enough for the legend's panel to stay open beside the land. */
const ROOMY_QUERY = '(min-width: 1000px) and (hover: hover)';
/**
 * A biome's name keeps out of this box around every landmark's name, in
 * screen pixels (its two lines are much wider than tall, so the box is
 * too); turned into tiles once the scale is known, since a bigger world
 * draws each tile smaller.
 */
const LABEL_CLEARANCE_PX = { x: 150, y: 60 };
/** The sides a name tries around its mark, in order (`data-side`, world-map.css). */
const SIDES = ['below', 'above', 'right', 'left'] as const;
/** Screen pixels kept free between two names, so they never touch. */
const NAME_GAP = 2;
/** How many places a biome's name may try, deepest first. */
const BIOME_ANCHORS = 40;
/** Labels stay this far inside the map's edge, in screen pixels. */
const EDGE_MARGIN = 3;

/**
 * A reading of the band, not a rule: past its top a region is behind the
 * player, one level short of it is already the right place, four short a
 * stretch.
 */
export function challengeOf(level: number, band: { min: number; max: number }): Challenge {
  if (level > band.max) return 'easy';
  if (level >= band.min - 1) return 'fit';
  if (level >= band.min - 4) return 'hard';
  return 'deadly';
}

export class WorldMap {
  private readonly root: HTMLElement;
  private readonly stage: HTMLElement;
  /** What moves under the finger: the canvas and everything drawn over it. */
  private readonly surface: HTMLElement;
  private readonly side: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly labels: HTMLElement;
  private readonly marker: HTMLElement;
  /** The other players the scene knows of (in sight, or listed for an admin), a marker and a name each. */
  private readonly playerLayer: HTMLElement;
  private players: readonly MapPlayer[] = [];
  /** An admin's map asks for the zone's players at each opening. */
  private onOpen: (() => void) | undefined;
  /** The friends in this zone (ADR 0033), a marker and a name each. */
  private readonly friendLayer: HTMLElement;
  private friends: readonly { id: string; name: string; x: number; y: number }[] = [];
  /**
   * The quests on the map (ADR 0047), painted like the minimap's: the
   * townsfolk's « ! » and « ? », and where each quest under way leads.
   */
  private questMarks: readonly MinimapMarker[] = [];
  private questTargets: readonly { x: number; y: number }[] = [];
  private questsStale = false;
  /** The village a world event falls on (ADR 0050), marked like a battle. */
  private event: { x: number; y: number } | null = null;
  /** The next events, under the legend; rebuilt at each opening. */
  private agenda: HTMLElement;
  private readonly legend: HTMLElement;
  private readonly advice: HTMLElement;
  private readonly button: HTMLButtonElement;
  /** One pixel a tile, each biome in its colour, for the wash. */
  private readonly biomePicture: HTMLCanvasElement;
  /** Where each biome's name may be written, in tiles, the best first. */
  private readonly anchors = new Map<BiomeId, { x: number; y: number }[]>();
  private readonly present: BiomeId[];
  /** CSS pixels a tile: whole on a desktop, maybe a fraction on a phone (see `layout`). */
  private scale = 0;
  /** Canvas pixels a tile, always whole. */
  private pixelScale = 0;
  private level = 1;
  /** Places the player's story names otherwise, by their name in the zone. */
  private readonly shownNames = new Map<string, string>();
  private highlighted: BiomeId | undefined;
  private self: { x: number; y: number } | undefined;
  /** Whether the map fills the screen and moves under the finger (ADR 0086). */
  private exploring = false;
  private readonly pan: MapPan;
  private readonly controls: ExploreControls;
  /** The explorer's zoom steps and the one shown. */
  private levels: number[] = [EXPLORE_SCALE];
  private zoom = 0;
  private hereBiome: BiomeId | undefined;
  private readonly onResize = (): void => {
    if (this.isOpen) this.arrange();
  };
  /** A game master's teleport (ADR 0025): a click on the map names a tile. */
  private onPick: ((tile: { x: number; y: number }) => void) | undefined;
  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
    if (document.activeElement instanceof HTMLInputElement) return;
    const key = event.key.toLowerCase();
    if (key === 'escape' && !this.root.hidden) {
      this.toggle(false);
      return;
    }
    if (!isAction(event, 'map')) return;
    event.preventDefault();
    this.toggle();
  };

  constructor(private readonly options: WorldMapOptions) {
    const { zone } = options;
    this.biomePicture = paintBiomes(zone);
    this.present = BIOME_IDS.filter((id) => zone.biomes?.some((row) => row.includes(digit(id))));

    this.root = document.createElement('section');
    this.root.className = 'world-map';
    this.root.hidden = true;
    this.root.setAttribute('aria-label', uiText('Carte du monde'));

    const title = document.createElement('h2');
    title.className = 'world-map__title';
    kitTitle(title, 'map');
    title.textContent = uiText(zone.name);
    const close = closeButton('Fermer (Échap)', () => {
      this.toggle(false);
    });
    title.append(close);

    this.stage = document.createElement('div');
    this.stage.className = 'world-map__stage';
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'world-map__canvas';
    this.labels = document.createElement('div');
    this.labels.className = 'world-map__labels';
    this.marker = document.createElement('span');
    this.marker.className = 'world-map__self';
    this.marker.title = uiText('Toi');
    this.playerLayer = document.createElement('div');
    this.playerLayer.className = 'world-map__players';
    this.friendLayer = document.createElement('div');
    this.friendLayer.className = 'world-map__friends';
    this.surface = document.createElement('div');
    this.surface.className = 'world-map__surface';
    this.surface.append(this.canvas, this.labels, this.playerLayer, this.friendLayer, this.marker);
    this.stage.append(this.surface);
    this.pan = new MapPan(
      this.stage,
      this.surface,
      (step, focus) => {
        this.zoomBy(step, focus);
      },
      () => this.visibleArea(),
    );
    // On the whole stage, not the canvas: a hovered place's diamond and the
    // player's own marker sit above it and would swallow the click.
    this.stage.addEventListener('click', (event) => {
      if (this.onPick === undefined || this.scale === 0) return;
      const box = this.canvas.getBoundingClientRect();
      const x = Math.floor((event.clientX - box.left) / this.scale);
      const y = Math.floor((event.clientY - box.top) / this.scale);
      if (x < 0 || y < 0 || x >= zone.width || y >= zone.height) return;
      this.onPick({ x, y });
      this.toggle(false);
    });

    const side = document.createElement('div');
    side.className = 'world-map__side';
    this.side = side;
    const heading = document.createElement('h3');
    heading.className = 'world-map__heading world-map__heading--regions';
    heading.textContent = uiText('Régions');
    this.legend = document.createElement('ul');
    this.legend.className = 'world-map__legend';
    this.advice = document.createElement('p');
    this.advice.className = 'world-map__advice';
    const keys = document.createElement('p');
    keys.className = 'world-map__keys';
    for (const [className, text] of [
      ['world-map__key world-map__key--village', 'Village'],
      ['world-map__key world-map__key--place', "Lieu d'intérêt"],
      ['world-map__key world-map__key--lair', 'Repaire de boss'],
      ['world-map__key world-map__key--self', 'Toi'],
    ] as const) {
      const key = document.createElement('span');
      key.className = className;
      key.textContent = uiText(text);
      keys.append(key);
    }
    const portalKey = document.createElement('span');
    portalKey.className = 'world-map__key world-map__key--portal';
    const portalIcon = document.createElement('span');
    portalIcon.className = 'map-portal world-map__key-icon';
    portalKey.append(portalIcon, uiText('Portail'));
    keys.insertBefore(portalKey, keys.lastElementChild);
    this.agenda = eventAgenda();
    this.controls = exploreControls((tab) => {
      this.showTab(tab);
    });
    side.append(this.controls.tabs, heading, this.legend, this.advice, keys, this.agenda);
    this.showTab('regions');
    this.controls.zoomIn.addEventListener('click', () => {
      this.zoomBy(1);
    });
    this.controls.zoomOut.addEventListener('click', () => {
      this.zoomBy(-1);
    });
    this.controls.recentre.addEventListener('click', () => {
      this.centreOn(this.self);
    });
    this.controls.here.addEventListener('click', () => {
      this.openSheet(!this.root.classList.contains('world-map--sheet'));
    });

    this.root.append(title, this.stage, side, this.controls.zoom, this.controls.here);
    document.body.append(this.root);

    this.button = hudShortcutButton('map', 'Carte', 'map');
    this.button.addEventListener('click', () => {
      this.toggle();
    });
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('resize', this.onResize);
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  toggle(open = this.root.hidden): void {
    this.root.hidden = !open;
    if (!open) return;
    this.onOpen?.();
    // Each opening starts on the player, a region's worth of land around them.
    this.zoom = -1;
    this.openSheet(false);
    this.arrange();
    // A desktop window has room for the legend beside the land: open it there.
    if (this.exploring && window.matchMedia(ROOMY_QUERY).matches) this.openSheet(true);
    // The journal moved while the map was closed: its marks follow now.
    if (this.questsStale) this.paint();
    this.renderLegend();
    const agenda = eventAgenda();
    this.agenda.replaceWith(agenda);
    this.agenda = agenda;
    this.showTab(this.side.dataset.tab as ExploreTab);
    this.placeMarker();
    if (this.exploring) {
      this.showHere();
      this.centreOn(this.self);
    }
  }

  setLevel(level: number): void {
    if (level === this.level) return;
    this.level = level;
    this.hereBiome = undefined;
    if (!this.isOpen) return;
    this.renderLegend();
    if (this.exploring) this.showHere();
  }

  /** A place named as the player's story has it (the farm before the fire, ADR 0067). */
  renamePlace(name: string, shown: string): void {
    if ((this.shownNames.get(name) ?? name) === shown) return;
    this.shownNames.set(name, shown);
    if (this.isOpen) this.renderLabels();
  }

  private nameOf(mark: { name: string }): string {
    return uiText(this.shownNames.get(mark.name) ?? mark.name);
  }

  /**
   * For an admin: a click on the map asks to be there. The server checks the
   * right and finds open ground; the map only names the tile.
   */
  setPick(onPick: (tile: { x: number; y: number }) => void): void {
    this.onPick = onPick;
    this.root.classList.add('world-map--pick');
    this.canvas.title = uiText('[GM] Clic : s’y téléporter');
  }

  /** The other players to draw, as `mapPlayers` gathers them. */
  setPlayers(players: readonly MapPlayer[]): void {
    this.players = players;
    if (this.isOpen) this.placeMarker();
  }

  /** For an admin: called at each opening, to ask the server who is in the zone. */
  setOnOpen(onOpen: () => void): void {
    this.onOpen = onOpen;
  }

  /** The tiles of the friends in this zone, as `friend_positions` last said. */
  setFriends(friends: readonly { id: string; name: string; x: number; y: number }[]): void {
    this.friends = friends;
    if (this.isOpen) this.placeMarker();
  }

  /** The quests' marks and targets, as the journal says (ADR 0047). */
  setQuests(marks: readonly MinimapMarker[], targets: readonly { x: number; y: number }[]): void {
    this.questMarks = marks;
    this.questTargets = targets;
    if (this.isOpen) this.paint();
    else this.questsStale = true;
  }

  /** The village under a world event, or null once it is over (ADR 0050). */
  setEvent(event: { x: number; y: number } | null): void {
    this.event = event;
    if (this.isOpen) this.paint();
    else this.questsStale = true;
  }

  /** The player's tile; cheap, so the scene may call it every refresh. */
  setSelf(position: { x: number; y: number }): void {
    this.self = position;
    if (!this.isOpen) return;
    this.placeMarker();
    if (this.exploring) this.showHere();
  }

  destroy(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('resize', this.onResize);
    this.pan.destroy();
    this.button.remove();
    this.root.remove();
  }

  /**
   * An integer number of screen pixels a tile, the largest that fits the
   * window: any fraction would blur the map. Recomputed at each opening,
   * so a resized window gets a map that fits it.
   */
  private layout(): void {
    const { zone } = this.options;
    const { scale, pixelScale } = this.exploring
      ? explored(this.levels[this.zoom] ?? EXPLORE_SCALE)
      : crisp(this.room());
    if (scale === this.scale && pixelScale === this.pixelScale) return;
    this.scale = scale;
    this.pixelScale = pixelScale;
    this.canvas.width = zone.width * pixelScale;
    this.canvas.height = zone.height * pixelScale;
    const width = `${String(zone.width * scale)}px`;
    const height = `${String(zone.height * scale)}px`;
    this.canvas.style.width = width;
    this.canvas.style.height = height;
    this.surface.style.width = width;
    this.surface.style.height = height;
    // The explorer's stage is the screen (world-map.css); the framed map's is the map.
    this.stage.style.width = this.exploring ? '' : width;
    this.stage.style.height = this.exploring ? '' : height;
    const clearance = {
      x: Math.ceil(LABEL_CLEARANCE_PX.x / scale),
      y: Math.ceil(LABEL_CLEARANCE_PX.y / scale),
    };
    this.anchors.clear();
    for (const id of this.present) {
      this.anchors.set(id, labelAnchors(zone, id, clearance));
    }
    this.paint();
    this.renderLabels();
  }

  /** CSS pixels a tile the framed map would get from this window. */
  private framedScale(): number {
    return crisp(this.room()).scale;
  }

  /** Tiles' worth of room the window leaves the map, in CSS pixels a tile. */
  private room(): number {
    const { zone } = this.options;
    const width = window.innerWidth;
    const height = window.innerHeight;
    if (!window.matchMedia(COMPACT_QUERY).matches) {
      return Math.min((width - LEGEND_WIDTH) / zone.width, (height - CHROME_HEIGHT) / zone.height);
    }
    if (window.matchMedia(STACKED_QUERY).matches) {
      return Math.min(
        (width - COMPACT_CHROME.width) / zone.width,
        (height - COMPACT_CHROME.height - STACKED_TITLE_HEIGHT - STACKED_LEGEND_HEIGHT) /
          zone.height,
      );
    }
    return Math.min(
      (width - COMPACT_CHROME.width - COMPACT_LEGEND_WIDTH) / zone.width,
      (height - COMPACT_CHROME.height) / zone.height,
    );
  }

  /**
   * The map laid out for the screen as it is now: framed on a large screen,
   * the whole screen on a phone (ADR 0086). Called at each opening and at
   * each resize, a phone turned on its side included.
   */
  private arrange(): void {
    const { zone } = this.options;
    const exploring =
      window.matchMedia(EXPLORE_QUERY).matches || this.framedScale() < EXPLORE_SCALE;
    if (exploring !== this.exploring) {
      this.exploring = exploring;
      this.root.classList.toggle('world-map--explore', exploring);
      this.pan.enabled = exploring;
      if (!exploring) {
        this.pan.reset();
        this.openSheet(false);
      }
    }
    if (exploring) {
      const cover = coverScale(this.visibleArea(), zone, devicePixels());
      const shown = this.zoom < 0 ? EXPLORE_SCALE : this.scale;
      this.levels = zoomLevels(cover);
      this.zoom = levelNear(this.levels, shown);
    }
    this.layout();
    this.placeMarker();
    if (exploring) this.pan.moveTo(this.pan.x, this.pan.y);
  }

  /**
   * One zoom step in or out, about a point of the screen (a pinch's middle,
   * the mouse) or the middle of the land in view: what is under it stays.
   */
  private zoomBy(step: -1 | 1, focus?: { x: number; y: number }): void {
    const zoom = Math.min(this.levels.length - 1, Math.max(0, this.zoom + step));
    if (zoom === this.zoom || !this.exploring) return;
    const stage = this.stage.getBoundingClientRect();
    const view = this.visibleArea();
    const at = focus ?? { x: stage.left + view.width / 2, y: stage.top + view.height / 2 };
    const before = this.scale;
    const tileX = (at.x - stage.left - this.pan.x) / before;
    const tileY = (at.y - stage.top - this.pan.y) / before;
    this.zoom = zoom;
    this.layout();
    this.placeMarker();
    this.pan.moveTo(at.x - stage.left - tileX * this.scale, at.y - stage.top - tileY * this.scale);
  }

  /** A tile brought to the middle of the land in view (the panel may hide part of it). */
  private centreOn(tile: { x: number; y: number } | undefined): void {
    if (!this.exploring) return;
    const { zone } = this.options;
    const target = tile ?? { x: zone.width / 2, y: zone.height / 2 };
    const view = this.visibleArea();
    this.pan.moveTo(
      view.width / 2 - (target.x + 0.5) * this.scale,
      view.height / 2 - (target.y + 0.5) * this.scale,
    );
  }

  /**
   * The stage left uncovered by the open panel, from its top left corner:
   * the land fills it, so even the world's far edge comes out from under
   * the panel.
   */
  private visibleArea(): { width: number; height: number } {
    const stage = this.stage.getBoundingClientRect();
    let width = stage.width;
    let height = stage.height;
    if (this.root.classList.contains('world-map--sheet')) {
      const side = this.side.getBoundingClientRect();
      // Under the map upright, beside it on its side (world-map.css).
      if (side.top > stage.top + 1) height = side.top - stage.top;
      else width = side.left - stage.left;
    }
    return { width, height };
  }

  /** The legend's panel over the explorer, opened from the bar at the bottom. */
  private openSheet(open: boolean): void {
    this.root.classList.toggle('world-map--sheet', open);
    this.controls.here.setAttribute('aria-expanded', String(open));
    // The panel changes the land in view: the land covers what is left of it.
    if (this.exploring) this.arrange();
  }

  private showTab(tab: ExploreTab): void {
    this.side.dataset.tab = tab;
    for (const button of this.controls.tabs.querySelectorAll<HTMLElement>('.world-map__tab')) {
      const shown = button.dataset.tab === tab;
      button.classList.toggle('world-map__tab--shown', shown);
      button.setAttribute('aria-selected', String(shown));
    }
  }

  /** The bar at the bottom: the region underfoot, its levels and how it suits the player. */
  private showHere(): void {
    const biome = this.self === undefined ? undefined : biomeAt(this.options.zone, this.self);
    if (biome === this.hereBiome && biome !== undefined) return;
    this.hereBiome = biome;
    const { here, hereChip, hereName, hereTag } = this.controls;
    if (biome === undefined) {
      hereChip.hidden = true;
      hereName.textContent = uiText(this.options.zone.name);
      hereTag.textContent = '';
      here.className = 'world-map__here';
      return;
    }
    const { name, colour, level } = BIOMES[biome];
    const challenge = challengeOf(this.level, level);
    hereChip.hidden = false;
    hereChip.style.backgroundColor = colour;
    hereName.textContent = uiFormat('{region} · {levels}', {
      region: uiText(name),
      levels: uiText(levelLabel(level)),
    });
    hereTag.textContent = uiText(CHALLENGE_LABELS[challenge]);
    here.className = `world-map__here world-map__region--${challenge}`;
  }

  private paint(): void {
    const context = this.canvas.getContext('2d');
    if (context === null) return;
    this.questsStale = false;
    const { zone, picture } = this.options;
    const scale = this.pixelScale;
    const width = zone.width * scale;
    const height = zone.height * scale;
    context.imageSmoothingEnabled = false;
    context.globalAlpha = 1;
    context.drawImage(picture, 0, 0, width, height);

    // Forest and meadow share the same grass: the wash is what tells them apart.
    context.globalAlpha = this.highlighted === undefined ? 0.22 : 0.12;
    context.drawImage(this.biomePicture, 0, 0, width, height);
    if (this.highlighted !== undefined) {
      const only = paintBiomes(zone, this.highlighted);
      context.globalAlpha = 0.55;
      context.drawImage(only, 0, 0, width, height);
    }

    // The borders: a dark line wherever two neighbouring tiles differ.
    context.globalAlpha = 0.75;
    context.fillStyle = '#12121c';
    const line = Math.max(1, Math.floor(scale / 2));
    for (let y = 0; y < zone.height; y += 1) {
      for (let x = 0; x < zone.width; x += 1) {
        const here = zone.biomes?.[y]?.[x];
        if (x + 1 < zone.width && zone.biomes?.[y]?.[x + 1] !== here) {
          context.fillRect((x + 1) * scale - Math.floor(line / 2), y * scale, line, scale);
        }
        if (y + 1 < zone.height && zone.biomes?.[y + 1]?.[x] !== here) {
          context.fillRect(x * scale, (y + 1) * scale - Math.floor(line / 2), scale, line);
        }
      }
    }

    // The quests' marks and the event's, last: over the wash and the borders, so
    // nothing dims them (SHE-43). One pixel a tile, then scaled with the land.
    if (this.questMarks.length + this.questTargets.length > 0 || this.event !== null) {
      context.globalAlpha = 1;
      const marks = document.createElement('canvas');
      marks.width = zone.width;
      marks.height = zone.height;
      const pen = marks.getContext('2d');
      if (pen !== null) {
        paintQuestMarks(pen, this.questMarks);
        for (const target of this.questTargets) paintQuestTarget(pen, target.x, target.y);
        if (this.event !== null) paintEventMark(pen, this.event.x, this.event.y);
        context.drawImage(marks, 0, 0, width, height);
      }
    }
    context.globalAlpha = 1;
  }

  /**
   * The names on the map, in order of importance: the villages, the
   * Company's bastion, the biomes, the lairs, then the places of interest.
   * Every mark (arch, square, diamond) is reserved first, so no name ever
   * hides one; each name then tries below its mark, above, right, left. A
   * crowded world has more names than room: a place whose name finds no
   * free side keeps its mark and shows its name on hover only.
   */
  private renderLabels(): void {
    const { zone } = this.options;
    this.labels.replaceChildren();
    const stage = this.surface.getBoundingClientRect();
    const taken: DOMRect[] = [];
    const overlaps = (box: DOMRect, other: DOMRect, gap: number): boolean =>
      box.left < other.right + gap &&
      box.right + gap > other.left &&
      box.top < other.bottom + gap &&
      box.bottom + gap > other.top;
    // A name keeps NAME_GAP from everything but its own mark, which it only must not cover.
    const clashes = (box: DOMRect, own?: DOMRect): boolean =>
      (own !== undefined && overlaps(box, own, 0)) ||
      taken.some((other) => other !== own && overlaps(box, other, NAME_GAP));
    // A label near the edge is pushed back inside the map.
    const fit = (label: HTMLElement): DOMRect => {
      label.style.translate = '';
      const box = label.getBoundingClientRect();
      const dx =
        box.left < stage.left + EDGE_MARGIN
          ? stage.left + EDGE_MARGIN - box.left
          : box.right > stage.right - EDGE_MARGIN
            ? stage.right - EDGE_MARGIN - box.right
            : 0;
      const dy =
        box.top < stage.top + EDGE_MARGIN
          ? stage.top + EDGE_MARGIN - box.top
          : box.bottom > stage.bottom - EDGE_MARGIN
            ? stage.bottom - EDGE_MARGIN - box.bottom
            : 0;
      // `translate`, not a margin: a name left of or above its mark is held by `right` or `bottom`.
      if (dx !== 0 || dy !== 0)
        label.style.translate = `${String(Math.round(dx))}px ${String(Math.round(dy))}px`;
      return dx !== 0 || dy !== 0 ? label.getBoundingClientRect() : box;
    };
    const claim = (label: HTMLElement, own?: DOMRect): boolean => {
      const box = fit(label);
      if (clashes(box, own)) return false;
      taken.push(box);
      return true;
    };
    // A name beside its mark: the first free side wins.
    const place = (label: HTMLElement, own: DOMRect | undefined): boolean => {
      for (const side of SIDES) {
        label.dataset.side = side;
        if (claim(label, own)) return true;
      }
      label.dataset.side = 'below';
      fit(label);
      return false;
    };
    const quiet = (mark: HTMLElement, name: string): void => {
      mark.classList.add('world-map__mark--quiet');
      mark.title = this.nameOf({ name });
    };

    // The portals under every name, the favicon's arch like the minimap's.
    const arches = new Map<string, DOMRect>();
    for (const portal of zone.portals) {
      const mark = portalMark(portal.name);
      this.put(mark, portal);
      this.labels.append(mark);
      const box = mark.getBoundingClientRect();
      arches.set(`${String(portal.x)},${String(portal.y)}`, box);
      taken.push(box);
    }
    // Then every landmark's own mark, before any name is written.
    const marks = zone.landmarks.map((landmark) => {
      const arch =
        landmark.kind === 'village'
          ? arches.get(`${String(landmark.x)},${String(landmark.y)}`)
          : undefined;
      const portal = arch !== undefined;
      const company = landmark.name === COMPANY_QUARTER;
      const mark = document.createElement('div');
      mark.className = `world-map__mark world-map__mark--${landmark.kind}`;
      // A village's heart is its portal: the arch marks it, its name hangs beside it.
      if (portal) mark.classList.add('world-map__mark--portal');
      if (company) mark.classList.add('world-map__mark--company');
      // A quiet mark names itself on hover (world-map.css); a finger taps it.
      mark.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' ||
          this.pan.dragged ||
          !mark.classList.contains('world-map__mark--quiet')
        )
          return;
        mark.classList.toggle('world-map__mark--open');
      });
      this.put(mark, landmark);
      this.labels.append(mark);
      const own = arch ?? mark.getBoundingClientRect();
      if (!portal) taken.push(own);
      const label = document.createElement('div');
      label.className = 'world-map__label';
      const name = document.createElement('span');
      name.className = 'world-map__name';
      name.textContent = this.nameOf(landmark);
      label.append(name);
      // A lair says whose it is and how strong: a goal to come back for (ADR 0024).
      const who = landmark.kind === 'lair' ? BOSS_OF_LAIR.get(landmark.name) : undefined;
      let boss: HTMLElement | undefined;
      if (who !== undefined) {
        boss = document.createElement('span');
        boss.className = 'world-map__boss';
        boss.textContent = uiFormat('{boss} · niv. {level}', {
          boss: who.name,
          level: who.level ?? 1,
        });
        label.append(boss);
      }
      mark.append(label);
      return { landmark, mark, label, boss, company, own };
    });

    for (const { landmark, mark, label, own } of marks.filter(
      (m) => m.landmark.kind === 'village',
    )) {
      if (!place(label, own)) quiet(mark, landmark.name);
    }
    // The Company's bastion (ADR 0067), where the story gathers.
    for (const { landmark, mark, label, own } of marks.filter((m) => m.company)) {
      if (!place(label, own)) quiet(mark, landmark.name);
    }
    // A biome's name tries its few deepest open stretches, then without its
    // levels; the legend names it anyway, so a region with no room left goes without.
    for (const [id, anchors] of this.anchors) {
      const biome = BIOMES[id];
      const label = document.createElement('div');
      label.className = 'world-map__label world-map__label--biome';
      const name = document.createElement('span');
      name.textContent = uiText(biome.name);
      const level = document.createElement('span');
      level.className = 'world-map__level';
      level.textContent = uiText(levelLabel(biome.level));
      label.append(name, level);
      this.labels.append(label);
      const fits = (): boolean =>
        anchors.some((anchor) => {
          this.put(label, anchor);
          return claim(label);
        });
      if (fits()) continue;
      // Short of room, the name alone: the legend gives its levels.
      level.remove();
      if (!fits()) label.remove();
    }
    // A lair keeps its name; its boss's line goes first when room is short,
    // then the name itself, left to the hover like a place's.
    for (const { landmark, mark, label, boss, own } of marks.filter(
      (m) => m.landmark.kind === 'lair',
    )) {
      if (place(label, own)) continue;
      boss?.remove();
      if (place(label, own)) continue;
      quiet(mark, landmark.name);
    }
    for (const { landmark, mark, label, own } of marks.filter(
      (m) => m.landmark.kind === 'place' && !m.company,
    )) {
      if (!place(label, own)) quiet(mark, landmark.name);
    }
  }

  /** An element centred on a tile of the map. */
  private put(element: HTMLElement, tile: { x: number; y: number }): void {
    element.style.left = `${String(Math.round((tile.x + 0.5) * this.scale))}px`;
    element.style.top = `${String(Math.round((tile.y + 0.5) * this.scale))}px`;
  }

  private renderLegend(): void {
    const ordered = [...this.present].sort((a, b) => BIOMES[a].level.min - BIOMES[b].level.min);
    this.legend.replaceChildren();
    for (const id of ordered) {
      const biome = BIOMES[id];
      const challenge = challengeOf(this.level, biome.level);
      const row = document.createElement('li');
      row.className = `world-map__region world-map__region--${challenge}`;
      const chip = document.createElement('span');
      chip.className = 'world-map__chip';
      chip.style.backgroundColor = biome.colour;
      const text = document.createElement('span');
      text.className = 'world-map__region-text';
      const name = document.createElement('strong');
      name.textContent = uiText(biome.name);
      const detail = document.createElement('span');
      detail.textContent = uiFormat('{levels} · {village}', {
        levels: uiText(levelLabel(biome.level)),
        village: biome.village,
      });
      text.append(name, detail);
      const tag = document.createElement('span');
      tag.className = 'world-map__tag';
      tag.textContent = uiText(CHALLENGE_LABELS[challenge]);
      row.append(chip, text, tag);
      row.dataset.biome = id;
      row.classList.toggle('world-map__region--shown', this.highlighted === id);
      // A mouse shows a region while over its row. A finger has no hover: a
      // tap shows it, a second tap clears it. Pointer events tell the two
      // apart (a tap also fires emulated mouse events, which would undo it).
      row.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'mouse') this.highlight(id);
      });
      row.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.highlight(undefined);
      });
      row.addEventListener('pointerup', (event) => {
        if (event.pointerType !== 'mouse' && !this.exploring)
          this.highlight(this.highlighted === id ? undefined : id);
      });
      // On the explorer, a region is a place to go: the map flies to it.
      row.addEventListener('click', () => {
        if (!this.exploring) return;
        this.highlight(id);
        this.centreOn(this.anchors.get(id)?.[0]);
      });
      this.legend.append(row);
    }

    // The next place to go: the harshest region already at the player's level.
    const next = ordered.findLast((id) => challengeOf(this.level, BIOMES[id].level) === 'fit');
    const last = ordered.at(-1);
    const level = this.level;
    this.advice.textContent =
      next !== undefined
        ? uiFormat('Niveau {level} : cap sur {region}.', { level, region: BIOMES[next].name })
        : last !== undefined && level > BIOMES[last].level.max
          ? uiFormat('Niveau {level} : toute la vallée est à toi.', { level })
          : uiFormat('Niveau {level} : reste près de Havrebourg.', { level });
  }

  private highlight(id: BiomeId | undefined): void {
    if (id === this.highlighted) return;
    this.highlighted = id;
    for (const row of this.legend.querySelectorAll<HTMLElement>('.world-map__region')) {
      row.classList.toggle('world-map__region--shown', row.dataset.biome === id);
    }
    this.paint();
  }

  private placeMarker(): void {
    this.playerLayer.replaceChildren(
      ...this.players.map((player) => {
        const marker = document.createElement('span');
        marker.className = 'world-map__player';
        marker.style.left = `${String(Math.round((player.x + 0.5) * this.scale))}px`;
        marker.style.top = `${String(Math.round((player.y + 0.5) * this.scale))}px`;
        const name = document.createElement('span');
        name.className = 'world-map__player-name';
        name.textContent = player.name;
        marker.append(name);
        return marker;
      }),
    );
    this.friendLayer.replaceChildren(
      ...this.friends.map((friend) => {
        const marker = document.createElement('span');
        marker.className = 'world-map__friend';
        marker.style.left = `${String(Math.round((friend.x + 0.5) * this.scale))}px`;
        marker.style.top = `${String(Math.round((friend.y + 0.5) * this.scale))}px`;
        const name = document.createElement('span');
        name.className = 'world-map__friend-name';
        name.textContent = uiText(friend.name);
        marker.append(name);
        return marker;
      }),
    );
    const self = this.self;
    this.marker.hidden = self === undefined;
    if (self === undefined) return;
    this.marker.style.left = `${String(Math.round((self.x + 0.5) * this.scale))}px`;
    this.marker.style.top = `${String(Math.round((self.y + 0.5) * this.scale))}px`;
  }
}

/**
 * An integer number of screen pixels a tile, the largest within `room`: any
 * fraction would blur the map. A phone's screen may be too small for a whole
 * CSS pixel a tile: its dense screen still has whole *device* pixels to
 * give, so the canvas is drawn at device resolution and shown at 1/dpr
 * (crisp, never resampled).
 */
function crisp(room: number): { scale: number; pixelScale: number } {
  const dpr = devicePixels();
  const whole = Math.min(MAX_SCALE, Math.floor(room));
  const pixelScale = whole >= 1 ? whole : Math.max(1, Math.floor(room * dpr));
  return { scale: whole >= 1 ? whole : pixelScale / dpr, pixelScale };
}

/** Whole device pixels a CSS pixel. */
function devicePixels(): number {
  return Math.max(1, Math.floor(window.devicePixelRatio || 1));
}

/**
 * An explorer step on the canvas: a whole CSS pixel a tile is drawn at that
 * size and enlarged without smoothing; a cover between two is drawn at its
 * device pixels (whole by `coverScale`), shown at 1/dpr.
 */
function explored(scale: number): { scale: number; pixelScale: number } {
  if (Number.isInteger(scale)) return { scale, pixelScale: scale };
  return { scale, pixelScale: Math.round(scale * devicePixels()) };
}

function digit(id: BiomeId): string {
  return String(BIOME_IDS.indexOf(id));
}

/** Each tile in its biome's colour; with `only`, that biome alone. */
function paintBiomes(zone: ZoneDefinition, only?: BiomeId): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = zone.width;
  canvas.height = zone.height;
  const context = canvas.getContext('2d');
  if (context === null) return canvas;
  paintPixels(context, zone.width, zone.height, (x, y) => {
    const id = biomeAt(zone, { x, y });
    return id === undefined || (only !== undefined && id !== only) ? undefined : BIOMES[id].colour;
  });
  return canvas;
}

/**
 * Where a biome's name may be written, the best first: the tiles deepest
 * inside it (farthest from its border and the world's edge), clear of the
 * villages and places, which carry their own names, each a name's width
 * from the others. The meadow's heart is the capital, so its name moves out
 * into the fields around it; the map keeps the first that finds room.
 */
function labelAnchors(
  zone: ZoneDefinition,
  id: BiomeId,
  clearance: { x: number; y: number },
): { x: number; y: number }[] {
  const { width, height } = zone;
  const depth = new Int32Array(width * height).fill(-1);
  const queue: number[] = [];
  const inside = (x: number, y: number): boolean => biomeAt(zone, { x, y }) === id;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (!inside(x, y)) continue;
      const edge =
        x === 0 ||
        y === 0 ||
        x === width - 1 ||
        y === height - 1 ||
        !inside(x - 1, y) ||
        !inside(x + 1, y) ||
        !inside(x, y - 1) ||
        !inside(x, y + 1);
      if (!edge) continue;
      depth[y * width + x] = 0;
      queue.push(y * width + x);
    }
  }
  // A for-of over an array visits what is pushed during the loop: a BFS.
  for (const index of queue) {
    const x = index % width;
    const y = (index - x) / width;
    for (const [nx, ny] of [
      [x - 1, y],
      [x + 1, y],
      [x, y - 1],
      [x, y + 1],
    ] as const) {
      if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
      const next = ny * width + nx;
      if (depth[next] !== -1 || !inside(nx, ny)) continue;
      depth[next] = (depth[index] ?? 0) + 1;
      queue.push(next);
    }
  }
  const chosen: { x: number; y: number }[] = [];
  const near = (x: number, y: number, clear: number) => (spot: { x: number; y: number }) =>
    Math.abs(spot.x - x) < clearance.x * clear && Math.abs(spot.y - y) < clearance.y * clear;
  const deepest = (clear: number): { x: number; y: number } | undefined => {
    let best: { x: number; y: number } | undefined;
    let bestDepth = -1;
    for (let index = 0; index < depth.length; index += 1) {
      const d = depth[index] ?? -1;
      if (d <= bestDepth) continue;
      const x = index % width;
      const y = (index - x) / width;
      // Apart by an eighth of a name's box: tried one after the other, never together.
      if (chosen.some(near(x, y, 1 / 8))) continue;
      if (clear > 0 && zone.landmarks.some(near(x, y, clear))) continue;
      best = { x, y };
      bestDepth = d;
    }
    return best;
  };
  // A biome too crowded to clear every name by the full margin settles for
  // less, and at worst gets its name among them.
  for (const clear of [1, 0.66, 0.4, 0]) {
    while (chosen.length < BIOME_ANCHORS) {
      const spot = deepest(clear);
      if (spot === undefined) break;
      chosen.push(spot);
    }
  }
  return chosen;
}
